import { useState, useMemo } from 'react';
import { Tooltip, Box, Popover, IconButton } from '@material-ui/core';
import trI18n from '@/common/utils/trI18n';
import { tr } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';

import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import OverFlowTooltipWithTabsMui from '@/component/common/OverFlowTooltipWithTabsMui';
import TagDefaultBookmark from '../../../ticketKnowledge/elements/Bookmark/TagDefaultBookmark';
import Bookmark from '../../../ticketKnowledge/elements/Bookmark/Bookmark';
import UserTagPrivateBookmarkCount from '../../../ticketKnowledge/elements/Bookmark/UserTagPrivateBookmarkCount';
import TaggedBookmarkListName from '../../../ticketKnowledge/elements/Bookmark/TaggedBookmarkListName';
import CopyShortLink from '@/component/ticketKnowledge/elements/CopyShortLink';
import { PropTypes } from 'prop-types';
import useOnDemandOutlineDisplay from '@/component/multiLanguage/hooks/useOnDemandOutlineDisplay';

export default function PaginationButton({
    detailTicket,
    handleNextEmail,
    handlePrevEmail,
    handleGoToLatestEmail,
    currentPage,
    totalPages,
    isLatestEmail,
    handleAddPanelChatThread,
    currentEmailTicket,
    isGeneralUser,
    corporationDetail,
    selectedProject,
    currentUserDetail,
    reloadTicketListByPanelType,
    displayMode,
    setDisplayMode,
    setAnchorShowMoreActionsPopover,
    isArchiveOrTrashView,
    isCustomerOrDelivered
}) {
    // Re-render when the translation dictionary loads/extends so tr() text updates.
    useTranslationSubscription();

    let isCustomerMail =
        currentEmailTicket?.projectOrigin !== currentEmailTicket?.affiliationProject;

    const [anchorEl, setAnchorEl] = useState(null);
    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    const isDisableButtonPrev = currentPage === 1;
    const isDisableButtonNext = currentPage === totalPages;

    const genUpdatorByTicketType = () => {
        switch (currentEmailTicket?.ticketType) {
            case Constant.TICKET_TYPE.EMAIL:
                return currentEmailTicket?.createrUserName;
            default:
                if (currentEmailTicket?.deliverInfo?.status === Constant.DELIVER_STATUS.UNDELIVER) {
                    return currentEmailTicket?.updatorUserName;
                }
                return currentEmailTicket?.deliverInfo?.senderUserName;
        }
    };

    const genUpdatedTimeByTicketType = () => {
        switch (currentEmailTicket?.ticketType) {
            case Constant.TICKET_TYPE.EMAIL:
                return Utils.getDateTimeByFormatYYYYMMDDHHMM(currentEmailTicket?.updatedAt);
            default:
                if (currentEmailTicket?.deliverInfo?.status === Constant.DELIVER_STATUS.UNDELIVER) {
                    return Utils.getDateTimeByFormatYYYYMMDDHHMM(currentEmailTicket?.updatedAt);
                }
                return Utils.getDateTimeByFormatYYYYMMDDHHMM(
                    currentEmailTicket?.deliverInfo?.deliverAt
                );
        }
    };

    const bookmarkTargetType = useMemo(() => {
        return Utils.getBookmarkTargetTypeByTicketType(detailTicket?.ticketType);
    }, [detailTicket]);

    // On-demand translation: when the 「翻訳版」toggle is ON, show the translated
    // title; otherwise the original. Keyed by the email child currently viewed.
    const originalEmailOutline = !Utils.isEmptyString(detailTicket?.ticketOutline)
        ? detailTicket?.ticketOutline
        : Constant.EMAIL_TICKET_NO_TITLE;
    const { displayOutline: emailTitleToDisplay } = useOnDemandOutlineDisplay(
        originalEmailOutline,
        currentEmailTicket?.id,
    );

    return (
        <div className="email-ticket-header">
            <div className="email-ticket-header-inner">
                <TagDefaultBookmark
                    className={'ticket-knowledge-detail-tag-default'}
                    isViewDelete={!Utils.isEmptyString(detailTicket.deletedAt)}
                    ticketKnowledge={detailTicket}
                    corporationDetail={corporationDetail}
                    projectInuseDetail={selectedProject}
                    userDetail={currentUserDetail}
                    setTriggerReloadKnowledgeListPanel={reloadTicketListByPanelType}
                    bookmarkTargetType={bookmarkTargetType}
                />
                <Tooltip
                    title={emailTitleToDisplay}
                    placement="top"
                >
                    <label className="titleDisplay titleTicket-titlev2 title-clamp email-ticket-title title-knowledge-display">
                        {emailTitleToDisplay}
                    </label>
                </Tooltip>

                <div className="titleTicket-btn">
                    <div className="email-ticket-btn">
                        {/* Back To Latest Email Btn */}
                        {isLatestEmail && (
                            <div
                                className="back-to-latest-email-hyperlink-link"
                                onClick={() => handleGoToLatestEmail()}
                            >
                                {tr('最新に戻る')}
                            </div>
                        )}

                        {/* Next / Back Btn */}
                        <button
                            className={
                                isDisableButtonPrev
                                    ? 'emailPagi-btn emailPagi-btn-next disable'
                                    : 'emailPagi-btn emailPagi-btn-next'
                            }
                            onClick={() => handlePrevEmail()}
                            disabled={isDisableButtonPrev}
                        >
                            <PrevIcon isDisableButtonPrev={isDisableButtonPrev} />
                        </button>

                        <button
                            className={
                                isDisableButtonNext
                                    ? 'emailPagi-btn emailPagi-btn-prev disable'
                                    : 'emailPagi-btn emailPagi-btn-prev'
                            }
                            onClick={() => handleNextEmail()}
                            disabled={isDisableButtonNext}
                        >
                            <NextIcon isDisableButtonNext={isDisableButtonNext} />
                        </button>
                    </div>
                </div>
            </div>
            <div className="option-bookmark-ticket-email">
                <div className="ticket-email-bookmark-info">
                    <Bookmark
                        isTicketBookmark={true}
                        isViewDelete={!Utils.isEmptyString(detailTicket.deletedAt)}
                        ticketKnowledge={detailTicket}
                        isShowEdit={true}
                        panelType={Constant.PANEL_TYPE.HELPDESK_TICKET_DETAIL}
                        bookmarkPopupContainerClassName={'reset-position-bookmark-popup-container'}
                    />
                    <TaggedBookmarkListName
                        bookmarkListText={detailTicket?.bookmarkListText}
                        className="tagged-bookmark-list-text"
                    />
                </div>
                <div className="email-ticket-btn" style={{ gap: 5 }}>
                    {displayMode === Constant.DISPLAY_MODE.VIEW &&
                        !isArchiveOrTrashView &&
                        !isCustomerOrDelivered && (
                            <IconButton
                                onClick={() => setDisplayMode(Constant.DISPLAY_MODE.EDIT)}
                                className="itemNav"
                            >
                                <img src="/assets/image/icon-edit-mode.svg" alt="" />
                            </IconButton>
                        )}
                    {!Utils.isEmptyObject(detailTicket?.dataShareLink) && (
                        <CopyShortLink
                            detailTicket={detailTicket}
                            isGeneralUser={isGeneralUser}
                            corporationDetail={corporationDetail}
                        />
                    )}
                    <div style={{ paddingLeft: 2 }}>
                        <UserTagPrivateBookmarkCount ticketKnowledge={detailTicket} />
                    </div>
                    <IconButton
                        onClick={e => setAnchorShowMoreActionsPopover(e.currentTarget)}
                        className="itemNav"
                    >
                        <img src="/assets/image/icon-more-panel.svg" alt="" />
                    </IconButton>
                </div>
            </div>

            {/* Email-Ticket-Sub-Info */}
            {!isCustomerMail && (
                <div>
                    <Box
                        className="email-ticket-sub-info-icon"
                        aria-describedby={id}
                        variant="contained"
                        onClick={handleClick}
                    >
                        <DownArrowIcon />
                    </Box>
                    <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left'
                        }}
                    >
                        <div className="email-ticket-sub-info-wrap">
                            <div className="email-info-inner">
                                <div className="email-info-item">
                                    <label className="email-info-label">{`${trI18n(
                                        'CHAT_MESSAGE_CREATOR'
                                    )} : `}</label>
                                    <OverFlowTooltipWithTabsMui
                                        className="email-info-value"
                                        value={currentEmailTicket?.createrUserName || ''}
                                    />
                                </div>
                                <div className="email-info-item">
                                    <label className="email-info-label">{`${trI18n(
                                        'CHAT_MESSAGE_CREATED_TIME'
                                    )} : `}</label>
                                    <p className="email-info-value">
                                        {Utils.getDateTimeByFormatYYYYMMDDHHMM(
                                            currentEmailTicket?.createdAt
                                        )}
                                    </p>
                                </div>
                            </div>

                            <div className="email-info-inner">
                                <div className="email-info-item">
                                    <label className="email-info-label">{`${trI18n(
                                        'CHAT_MESSAGE_UPDATOR'
                                    )} : `}</label>
                                    <OverFlowTooltipWithTabsMui
                                        className="email-info-value"
                                        value={genUpdatorByTicketType()}
                                    />
                                </div>
                                <div className="email-info-item">
                                    <label className="email-info-label">{`${trI18n(
                                        'CHAT_MESSAGE_UPDATED_TIME'
                                    )} : `}</label>
                                    <p className="email-info-value">
                                        {genUpdatedTimeByTicketType()}
                                    </p>
                                </div>
                            </div>

                            <div className="email-info-inner">
                                <div className="email-info-item">
                                    <label className="email-info-label">{`${trI18n(
                                        'CHAT_MESSAGE_SENDER'
                                    )} : `}</label>
                                    <OverFlowTooltipWithTabsMui
                                        className="email-info-value"
                                        value={
                                            currentEmailTicket?.deliverInfo?.senderUserName || ''
                                        }
                                    />
                                </div>

                                <div className="email-info-item">
                                    <label className="email-info-label">{`${trI18n(
                                        'CHAT_MESSAGE_SENT_TIME'
                                    )} : `}</label>
                                    <p className="email-info-value">
                                        {currentEmailTicket?.deliverInfo?.deliverAt
                                            ? Utils.getDateTimeByFormatYYYYMMDDHHMM(
                                                  currentEmailTicket?.deliverInfo?.deliverAt
                                              )
                                            : ''}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Popover>
                </div>
            )}
        </div>
    );
}

PaginationButton.propTypes = {
    displayMode: PropTypes.any,
    setDisplayMode: PropTypes.any,
    setAnchorShowMoreActionsPopover: PropTypes.any,
    isArchiveOrTrashView: PropTypes.any,
    currentEmailTicket: PropTypes.any,
    isCustomerOrDelivered: PropTypes.any
};

const PrevIcon = ({ isDisableButtonPrev }) => {
    return (
        <svg
            width="6"
            height="10"
            viewBox="0 0 6 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.47138 0.528758C5.73173 0.789108 5.73173 1.21122 5.47138 1.47157L1.94279 5.00016L5.47138 8.52876C5.73173 8.78911 5.73173 9.21122 5.47138 9.47157C5.21103 9.73192 4.78892 9.73192 4.52858 9.47157L0.528575 5.47157C0.268226 5.21122 0.268226 4.78911 0.528575 4.52876L4.52858 0.528758C4.78892 0.268409 5.21103 0.268409 5.47138 0.528758Z"
                fill={isDisableButtonPrev ? '#2E3138' : '#2D72D2'}
            />
        </svg>
    );
};

const NextIcon = ({ isDisableButtonNext }) => {
    return (
        <svg
            width="6"
            height="10"
            viewBox="0 0 6 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.528575 0.528758C0.788925 0.268409 1.21103 0.268409 1.47138 0.528758L5.47138 4.52876C5.73173 4.78911 5.73173 5.21122 5.47138 5.47157L1.47138 9.47157C1.21103 9.73192 0.788925 9.73192 0.528575 9.47157C0.268226 9.21122 0.268226 8.78911 0.528575 8.52876L4.05717 5.00016L0.528575 1.47157C0.268226 1.21122 0.268226 0.789108 0.528575 0.528758Z"
                fill={isDisableButtonNext ? '#2E3138' : '#2D72D2'}
            />
        </svg>
    );
};

const DownArrowIcon = ({}) => {
    return (
        <svg
            className="email-ticket-sub-info"
            width="11"
            height="8"
            viewBox="0 0 11 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M5.51462 7.01487L0.742253 0.282837L10.2432 0.252014L5.51462 7.01487Z"
                fill="#5F6368"
            />
        </svg>
    );
};
